import React, { useState, useEffect } from 'react';
import { Prospect } from '../../shared/types';
import { Utensils, Plus, ArrowRight } from 'lucide-react';

interface DigitalMenuDemoProps {
  prospect: Prospect;
}

export const DigitalMenuDemo: React.FC<DigitalMenuDemoProps> = ({ prospect }) => {
  const [cart, setCart] = useState<{ id: string; name: string; price: number; qty: number }[]>([]);
  const [activeCategory, setActiveCategory] = useState('Pratos Principais');

  useEffect(() => {
    window.scrollTo(0, 0);
    if (prospect.theme) {
      const root = document.documentElement;
      root.style.setProperty('--ym-prospect-primary', prospect.theme.primary_color || '#E11D48');
      root.style.setProperty('--ym-prospect-bg', prospect.theme.bg_color || '#4C0519');
      root.style.setProperty('--ym-prospect-surface', prospect.theme.surface_color || '#881337');
      root.style.setProperty('--ym-prospect-text', prospect.theme.text_color || '#FFF1F2');
    }
  }, [prospect]);

  const menuItems = [
    { id: '1', category: 'Pratos Principais', name: 'Filé Mignon ao Molho de Gorgonzola', desc: 'Acompanha risoto de açafrão e legumes grelhados no azeite de ervas.', price: 68.90, img: 'https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&q=80&w=600' },
    { id: '2', category: 'Pratos Principais', name: 'Massa Artesanal ao Pesto de Nozes', desc: 'Tagliatelle fresco com pesto de manjericão, nozes e queijo parmesão ralado.', price: 54.00, img: 'https://images.unsplash.com/photo-1551183053-bf91a1d81141?auto=format&fit=crop&q=80&w=600' },
    { id: '3', category: 'Bebidas & Drinks', name: 'Drink Especial da Casa (Sem Álcool)', desc: 'Infusão de frutas vermelhas, água com gás, xarope artesanal de hortelã.', price: 18.00, img: 'https://images.unsplash.com/photo-1513558161293-cdaf765ed2fd?auto=format&fit=crop&q=80&w=600' },
    { id: '4', category: 'Sobremesas', name: 'Petit Gâteau de Chocolate Amargo', desc: 'Acompanha sorvete artesanal de baunilha e calda morna de morango.', price: 28.00, img: 'https://images.unsplash.com/photo-1606313564200-e75d5e30476c?auto=format&fit=crop&q=80&w=600' },
  ];

  const addToCart = (item: typeof menuItems[0]) => {
    setCart((prev) => {
      const exists = prev.find((i) => i.id === item.id);
      if (exists) {
        return prev.map((i) => (i.id === item.id ? { ...i, qty: i.qty + 1 } : i));
      }
      return [...prev, { id: item.id, name: item.name, price: item.price, qty: 1 }];
    });
  };

  const total = cart.reduce((acc, item) => acc + item.price * item.qty, 0);

  const handleCheckout = () => {
    const itemsList = cart.map((i) => `${i.qty}x ${i.name} (R$ ${(i.price * i.qty).toFixed(2)})`).join('%0A');
    const msg = `*Pedido de Cardápio Digital — ${prospect.trade_name}*%0A%0A${itemsList}%0A%0A*Total: R$ ${total.toFixed(2)}*`;
    const phone = prospect.whatsapp || '554988416296';
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[var(--ym-prospect-bg)] text-[var(--ym-prospect-text)] font-sans pb-32">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[var(--ym-prospect-bg)]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[var(--ym-prospect-primary)] flex items-center justify-center text-white font-bold">
              <Utensils className="w-5 h-5" />
            </div>
            <div>
              <span className="font-extrabold text-lg text-white block leading-none">{prospect.trade_name}</span>
              <span className="text-[10px] opacity-70 uppercase tracking-widest">Cardápio Digital Oficial</span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Aberto para Pedidos
          </div>
        </div>
      </header>

      {/* Menu Categories Bar */}
      <div className="sticky top-20 z-40 bg-[var(--ym-prospect-surface)] border-b border-white/10 py-3 px-6">
        <div className="max-w-4xl mx-auto flex gap-3 overflow-x-auto no-scrollbar">
          {['Pratos Principais', 'Bebidas & Drinks', 'Sobremesas'].map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-full text-xs font-extrabold uppercase tracking-wider transition-all whitespace-nowrap ${
                activeCategory === cat ? 'bg-[var(--ym-prospect-primary)] text-white shadow-lg' : 'bg-white/5 text-slate-400 hover:text-white'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Items List */}
      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {menuItems
          .filter((i) => i.category === activeCategory)
          .map((item) => (
            <div key={item.id} className="p-6 rounded-3xl bg-[var(--ym-prospect-surface)] border border-white/10 flex flex-col sm:flex-row items-center gap-6 hover:border-[var(--ym-prospect-primary)] transition-all">
              <img src={item.img} alt={item.name} className="w-full sm:w-32 h-32 object-cover rounded-2xl shrink-0" />
              <div className="flex-1">
                <h3 className="font-bold text-white text-lg mb-1">{item.name}</h3>
                <p className="text-xs text-slate-400 font-light leading-relaxed mb-4">{item.desc}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-black text-[var(--ym-prospect-primary)]">R$ {item.price.toFixed(2)}</span>
                  <button
                    onClick={() => addToCart(item)}
                    className="px-4 py-2 rounded-xl bg-[var(--ym-prospect-primary)] text-white font-bold text-xs uppercase tracking-wider flex items-center gap-1.5 hover:opacity-90 transition-opacity"
                  >
                    <Plus className="w-4 h-4" /> Adicionar
                  </button>
                </div>
              </div>
            </div>
          ))}
      </main>

      {/* Sticky Bottom Cart Bar */}
      {cart.length > 0 && (
        <div className="fixed bottom-6 left-4 right-4 z-50 max-w-2xl mx-auto bg-slate-900 border border-white/20 p-4 rounded-2xl shadow-2xl flex items-center justify-between animate-fade-in">
          <div>
            <span className="text-xs text-slate-400 block">{cart.reduce((a, b) => a + b.qty, 0)} itens no pedido</span>
            <span className="text-xl font-black text-white">R$ {total.toFixed(2)}</span>
          </div>
          <button
            onClick={handleCheckout}
            className="px-6 py-3 rounded-xl bg-[var(--ym-prospect-primary)] text-white font-black text-xs uppercase tracking-wider flex items-center gap-2 hover:opacity-90 transition-opacity"
          >
            Finalizar no WhatsApp <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};
